/**
 * Shared models — what the room builds after looking at everyone's own map.
 *
 * A shared model is one picture the group can point to later. A room can hold
 * several, related in two ways:
 *
 *   Views     — models side by side at the same place are different valid ways
 *               of seeing the same thing. None replaces another.
 *   Altitude  — "Go deeper" on one shape opens a model of just that shape, one
 *               level down (1 Vision map → 2 Architecture map → 3 Process flow
 *               → 4 Implementation blueprint). The trail at the top climbs back.
 *
 * One person edits a model at a time: whoever holds the pen. Anyone can take it.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { ChevronRight, Columns2, Download, PenLine, Plus, X } from 'lucide-react';

import { Select, TextInput } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { downloadMap } from './download';
import { Legend } from './Legend';
import { SaveToSpace } from './SaveToSpace';
import { MapEditor, MapPicker, MapView, type DeeperActions } from './MapCanvas';
import type { MapRoom } from './MentalMapRoom';
import {
  EMPTY_MAP,
  MAP_LEVELS,
  STAGE_MAX,
  copyInto,
  deeperModels,
  mapLevel,
  modelAncestry,
  siblingModels,
  subsetDoc,
  stageLayout,
  type MapDoc,
  type MapLevelNumber,
  type SharedModel,
} from './model';

export interface NewSharedModel {
  title: string;
  level: MapLevelNumber;
  parentModelId?: string;
  parentShapeId?: string;
}

export interface SharedRoom {
  models: SharedModel[];
  /** Resolves to the new model's id, or null if it could not be added. */
  createModel: (input: NewSharedModel) => Promise<string | null>;
  /** Resolves to the model as last saved, or null if the pen could not be taken. */
  takePen: (modelId: string) => Promise<MapDoc | null>;
  saveModel: (modelId: string, doc: MapDoc) => void;
}

const EYEBROW = 'font-mono text-xs uppercase tracking-wider text-[var(--text-dim)]';

function LevelBadge({ level }: { level: number }) {
  const meta = mapLevel(level);
  return (
    <span className="whitespace-nowrap rounded-full border border-[var(--rule-bright)] px-2 py-0.5 font-mono text-xs text-[var(--text-sec)]" title={meta.question}>
      Level {meta.level} · {meta.name}
    </span>
  );
}

function NewModelForm({
  heading,
  defaultTitle,
  fixedLevel,
  onCreate,
  onCancel,
}: {
  heading: string;
  defaultTitle: string;
  /** Set when the level is decided by where the model sits; otherwise the person picks. */
  fixedLevel?: MapLevelNumber;
  onCreate: (title: string, level: MapLevelNumber) => Promise<void>;
  onCancel?: () => void;
}) {
  const [title, setTitle] = useState(defaultTitle);
  const [level, setLevel] = useState<MapLevelNumber>(fixedLevel ?? 1);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    await onCreate(title.trim(), fixedLevel ?? level);
    setBusy(false);
  };

  return (
    <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void submit(e)}>
      <label className="min-w-56 flex-1 font-body text-sm text-[var(--text-sec)]">
        {heading}
        <TextInput className="mt-1" value={title} maxLength={80} autoFocus onChange={(e) => setTitle(e.target.value)} />
      </label>
      {!fixedLevel && (
        <label className="font-body text-sm text-[var(--text-sec)]">
          How high-level is it?
          <Select className="mt-1" value={String(level)} onChange={(e) => setLevel(Number(e.target.value) as MapLevelNumber)}>
            {MAP_LEVELS.map((l) => (
              <option key={l.level} value={l.level}>
                Level {l.level} · {l.name} — {l.question}
              </option>
            ))}
          </Select>
        </label>
      )}
      <Button type="submit" variant="cta" disabled={!title.trim() || busy}>
        {busy ? 'Adding…' : 'Add it'}
      </Button>
      {onCancel && (
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      )}
    </form>
  );
}

/** The views of the same thing, next to each other (up to four). */
function SideBySide({ models, onOpen }: { models: SharedModel[]; onOpen: (id: string) => void }) {
  const tiles = models.slice(0, STAGE_MAX);
  const layout = stageLayout(tiles.length);
  return (
    <div
      className="grid h-full min-h-0 gap-2 bg-[var(--surface)] p-2"
      style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gridTemplateRows: 'repeat(2, minmax(0, 1fr))' }}
    >
      {tiles.map((m, i) => (
        <section
          key={m.id}
          className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-[var(--rule)] bg-[var(--bg-raised)]"
          style={{ gridColumn: layout[i].column, gridRow: layout[i].row }}
        >
          <header className="flex items-center gap-2 border-b border-[var(--rule)] px-3 py-1.5">
            <h3 className="min-w-0 flex-1 truncate font-heading text-sm font-semibold text-[var(--text-pri)]">{m.title}</h3>
            <Button variant="ghost" size="sm" className="!px-1 !py-0" onClick={() => onOpen(m.id)}>
              Open
            </Button>
          </header>
          <div className="min-h-0 flex-1">
            <MapView doc={m.doc} interactive />
          </div>
        </section>
      ))}
    </div>
  );
}

export function SharedModels({ room, shared }: { room: MapRoom; shared: SharedRoom }) {
  const { models } = shared;
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [adding, setAdding] = useState<'view' | null>(null);
  const [sideBySide, setSideBySide] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [savingToSpace, setSavingToSpace] = useState(false);
  // Whose map is open beside the shared model, and which of its shapes are picked.
  const [referenceId, setReferenceId] = useState<string | null>(null);
  const [pickedIds, setPickedIds] = useState<string[]>([]);

  // While I hold the pen, the editor is the truth for this model: it starts
  // from one map and is remounted only when I replace the map wholesale.
  const [session, setSession] = useState<{ modelId: string; key: number; startDoc: MapDoc } | null>(null);
  const latestDoc = useRef<MapDoc>(EMPTY_MAP);

  const current = models.find((m) => m.id === currentId) ?? models.find((m) => m.parentModelId === null) ?? models[0] ?? null;
  const iHoldPen = current !== null && current.penHolderId === room.meId;

  useEffect(() => {
    if (!current) {
      if (session) setSession(null);
      return;
    }
    if (iHoldPen && session?.modelId !== current.id) {
      latestDoc.current = current.doc;
      setSession({ modelId: current.id, key: 0, startDoc: current.doc });
    } else if (!iHoldPen && session) {
      setSession(null);
    }
  }, [current, iHoldPen, session]);

  const open = useCallback((id: string) => {
    setCurrentId(id);
    setSideBySide(false);
    setAdding(null);
    setNotice(null);
  }, []);

  const ancestry = useMemo(() => (current ? modelAncestry(models, current.id) : []), [models, current]);
  const views = useMemo(() => (current ? siblingModels(models, current) : []), [models, current]);

  const deeper = useMemo<DeeperActions | undefined>(() => {
    if (!current) return undefined;
    return {
      canStart: current.level < 4,
      has: (shapeId) => deeperModels(models, current.id, shapeId).length > 0,
      open: (shapeId) => {
        const existing = deeperModels(models, current.id, shapeId);
        if (existing.length > 0) {
          open(existing[0].id);
          return;
        }
        if (current.level >= 4) return;
        const doc = session?.modelId === current.id ? latestDoc.current : current.doc;
        const label = doc.shapes.find((s) => s.id === shapeId)?.label.trim();
        void shared
          .createModel({
            title: label || 'A closer look',
            level: (current.level + 1) as MapLevelNumber,
            parentModelId: current.id,
            parentShapeId: shapeId,
          })
          .then((id) => (id ? open(id) : setNotice('The deeper model could not be started. Save has to reach the room first — try again in a moment.')));
      },
    };
  }, [current, models, open, session, shared]);

  const takePen = async () => {
    if (!current) return;
    const doc = await shared.takePen(current.id);
    if (doc === null) setNotice('Could not take the pen.');
  };

  const copyFrom = (doc: MapDoc) => {
    if (!current || !session || session.modelId !== current.id) return;
    const next = copyInto(latestDoc.current, doc);
    latestDoc.current = next;
    setSession({ modelId: current.id, key: session.key + 1, startDoc: next });
    shared.saveModel(current.id, next);
  };

  const visiblePeople = room.people.filter((p) => p.doc !== null && p.doc.shapes.length > 0);
  const reference = visiblePeople.find((p) => p.id === referenceId) ?? null;
  const penHolderName = current?.penHolderId ? (room.people.find((p) => p.id === current.penHolderId)?.name ?? 'Someone') : null;

  if (!current) {
    return (
      <div className="mx-auto flex h-full max-w-2xl flex-col justify-center gap-4 px-6">
        <p className={EYEBROW}>Shared model</p>
        <h2 className="font-heading text-2xl font-semibold text-[var(--text-pri)]">Build the picture you will all point to</h2>
        <p className="font-body text-sm text-[var(--text-sec)]">
          Once you have looked at each other&rsquo;s maps and talked them through, draw one together. It can be a process, a
          system, or simply how you think about something. You can add other valid views beside it later, and go deeper on any
          shape.
        </p>
        <NewModelForm
          heading="What is this a model of?"
          defaultTitle={room.prompt}
          onCreate={async (title, level) => {
            const id = await shared.createModel({ title, level });
            if (id) open(id);
            else setNotice('The shared model could not be started.');
          }}
        />
        {notice && <p className="font-body text-sm text-[var(--coral)]">{notice}</p>}
      </div>
    );
  }

  const parent = ancestry.length > 1 ? ancestry[ancestry.length - 2] : null;
  const parentShapeLabel = parent ? parent.doc.shapes.find((s) => s.id === current.parentShapeId)?.label.trim() : undefined;

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="space-y-2 border-b border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-2">
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
          {ancestry.map((m, i) => (
            <span key={m.id} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-4 w-4 text-[var(--text-dim)]" aria-hidden="true" />}
              {m.id === current.id ? (
                <span className="font-heading text-sm font-semibold text-[var(--text-pri)]">{m.title}</span>
              ) : (
                <Button variant="ghost" size="sm" className="!px-1 !py-0" title={`Back up to level ${m.level}`} onClick={() => open(m.id)}>
                  {m.title}
                </Button>
              )}
            </span>
          ))}
          <span className="ml-2">
            <LevelBadge level={current.level} />
          </span>
          {parent && (
            <span className="ml-2 font-body text-xs text-[var(--text-dim)]">
              A closer look at {parentShapeLabel ? `“${parentShapeLabel}”` : 'one shape'} in {parent.title}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className={EYEBROW}>{views.length > 1 ? 'Views of this' : 'One view so far'}</span>
          {views.map((m) => (
            <Button key={m.id} variant={m.id === current.id && !sideBySide ? 'cta' : 'secondary'} size="sm" onClick={() => open(m.id)}>
              {m.title}
            </Button>
          ))}
          <Button variant="ghost" size="sm" onClick={() => setAdding(adding ? null : 'view')} title="Another valid way of seeing the same thing — it sits beside this one, it does not replace it">
            <Plus className="h-4 w-4" /> Another view
          </Button>
          {views.length > 1 && (
            <Button variant={sideBySide ? 'cta' : 'ghost'} size="sm" onClick={() => setSideBySide(!sideBySide)}>
              <Columns2 className="h-4 w-4" /> Side by side
            </Button>
          )}
          <span className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            title="Save this model as a picture you can keep or drop into slides"
            onClick={() => downloadMap(session?.modelId === current.id ? latestDoc.current : current.doc, current.title)}
          >
            <Download className="h-4 w-4" /> Save as picture
          </Button>
          {room.canSaveToSpace && (
            <Button variant="ghost" size="sm" onClick={() => setSavingToSpace(true)} title="Keep this model, under a name, in your own space or a team space">
              Save to a space
            </Button>
          )}
          {iHoldPen ? (
            <span className="inline-flex items-center gap-1.5 font-body text-sm text-[var(--text-sec)]">
              <PenLine className="h-4 w-4" /> You have the pen
            </span>
          ) : (
            <>
              <span className="font-body text-sm text-[var(--text-sec)]">{penHolderName ? `${penHolderName} has the pen` : 'Nobody has the pen'}</span>
              <Button variant="cta" size="sm" onClick={() => void takePen()}>
                <PenLine className="h-4 w-4" /> Take the pen
              </Button>
            </>
          )}
        </div>

        {adding === 'view' && (
          <NewModelForm
            heading="Name the other view"
            defaultTitle=""
            fixedLevel={current.level}
            onCancel={() => setAdding(null)}
            onCreate={async (title) => {
              const id = await shared.createModel({
                title,
                level: current.level,
                parentModelId: current.parentModelId ?? undefined,
                parentShapeId: current.parentShapeId ?? undefined,
              });
              if (id) open(id);
              else setNotice('That view could not be added.');
            }}
          />
        )}
        {notice && <p className="font-body text-sm text-[var(--coral)]">{notice}</p>}
      </div>

      {room.canSaveToSpace && (
        <SaveToSpace
          open={savingToSpace}
          onClose={() => setSavingToSpace(false)}
          doc={session?.modelId === current.id ? latestDoc.current : current.doc}
          defaultName={current.title}
          level={current.level}
        />
      )}

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1">
          {sideBySide ? (
            <SideBySide models={views} onOpen={open} />
          ) : session && session.modelId === current.id ? (
            <MapEditor
              key={`${current.id}:${session.key}`}
              initialDoc={session.startDoc}
              deeper={deeper}
              legend={(api) => <Legend {...api} />}
              onChange={(doc) => {
                latestDoc.current = doc;
                shared.saveModel(current.id, doc);
              }}
            />
          ) : (
            <MapView doc={current.doc} interactive deeper={deeper} />
          )}
        </div>

        {!sideBySide && reference && (
          <section className="flex w-[38%] min-w-72 shrink-0 flex-col border-l border-[var(--rule)] bg-[var(--bg-raised)]">
            <header className="flex flex-wrap items-center gap-2 border-b border-[var(--rule)] px-3 py-2">
              <h2 className="min-w-0 flex-1 truncate font-heading text-sm font-semibold text-[var(--text-pri)]">
                {reference.id === room.meId ? 'Your map' : `${reference.name}'s map`}
              </h2>
              <Button
                variant="cta"
                size="sm"
                disabled={!iHoldPen || pickedIds.length === 0}
                title={iHoldPen ? 'Copy the picked shapes, and the lines between them, into the shared model' : 'Take the pen to bring shapes across'}
                onClick={() => copyFrom(subsetDoc(reference.doc!, pickedIds))}
              >
                Bring {pickedIds.length > 0 ? `${pickedIds.length} picked` : 'picked'}
              </Button>
              <Button variant="secondary" size="sm" disabled={!iHoldPen} onClick={() => copyFrom(reference.doc!)}>
                Bring it all
              </Button>
              <Button variant="ghost" size="sm" className="!p-1" title="Close this map" aria-label="Close this map" onClick={() => setReferenceId(null)}>
                <X className="h-4 w-4" />
              </Button>
            </header>
            <p className="border-b border-[var(--rule)] px-3 py-1.5 font-body text-xs text-[var(--text-dim)]">
              {iHoldPen
                ? 'Click a shape to pick it, Shift-click to add another, or drag across several.'
                : 'Take the pen to bring shapes across. You can still look.'}
            </p>
            <div className="min-h-0 flex-1">
              <MapPicker key={reference.id} doc={reference.doc!} onPick={setPickedIds} />
            </div>
          </section>
        )}

        {!sideBySide && (
          <aside className="flex w-56 shrink-0 flex-col overflow-y-auto border-l border-[var(--rule)] bg-[var(--bg-raised)]">
            <div className="px-4 pt-4">
              <h2 className="font-heading text-base font-semibold text-[var(--text-pri)]">Everyone&rsquo;s maps</h2>
              <p className="mt-1 font-body text-xs text-[var(--text-dim)]">
                Click a map to open it beside the shared model, then pick the parts to bring across.
              </p>
            </div>
            <ul className="space-y-2 p-3">
              {visiblePeople.length === 0 && (
                <li className="font-body text-xs text-[var(--text-dim)]">No maps are visible yet.</li>
              )}
              {visiblePeople.map((person) => (
                <li key={person.id}>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={`Open ${person.name}'s map beside the shared model`}
                    aria-pressed={referenceId === person.id}
                    onClick={() => {
                      setPickedIds([]);
                      setReferenceId(referenceId === person.id ? null : person.id);
                    }}
                    className={`!block w-full overflow-hidden rounded-lg border !p-0 text-left !font-normal ${
                      referenceId === person.id ? 'border-[var(--text-pri)]' : 'border-[var(--rule)] hover:border-[var(--rule-bright)]'
                    }`}
                  >
                    <div className="pointer-events-none h-20">
                      <MapView doc={person.doc!} />
                    </div>
                    <div className="border-t border-[var(--rule)] px-3 py-1.5 font-heading text-sm font-semibold text-[var(--text-pri)]">
                      {person.id === room.meId ? `${person.name} (you)` : person.name}
                    </div>
                  </Button>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </div>
  );
}
